/**
 * Proposal Execution Service
 *
 * Executes approved agent proposals by calling the appropriate entity creation
 * functions from @trip-planner-monorepo/shared, then updates the suggestion
 * status via agentSuggestionsService.
 *
 * Service Registry:
 * Maps action.service + action.method to the actual Firestore function.
 * The Cloud Function writes these in the suggestion's action object:
 *   action.service  = 'accommodation' | 'travel' | 'place' | 'day'
 *   action.method   = 'create'
 *   action.data     = the entity data to pass to the function
 *
 * effectiveUserId Pattern:
 * - All Firestore writes use effectiveUserId (trip owner for shared trips)
 */

import {
  addAccommodation,
  updateAccommodation,
  addTravel,
  updateTravel,
  addPlace,
  updatePlace,
  addDay,
  updateDay,
  eventBridgeService,
  EVENT_TYPES
} from '@trip-planner-monorepo/shared';
import { agentSuggestionsService } from './agentSuggestionsService';

// ---------------------------------------------------------------------------
// Data Normalizers
// The AI generates flat JSON in PROPOSE tokens. Entity services expect
// specific nested structures. These normalizers bridge the gap.
// ---------------------------------------------------------------------------

/**
 * Normalize AI-generated flat accommodation data into the nested structure
 * that accommodationService / AccommodationList expect.
 *
 * AI flat format example:
 *   { name, address, checkIn, checkOut, type, city }
 * Required nested format:
 *   { type, details: { name, city, checkInDate, checkOutDate, address, ... } }
 */
function normalizeAccommodationData(rawData) {
  // Already in the correct nested format — pass through unchanged
  if (rawData && rawData.details && typeof rawData.details === 'object') {
    return rawData;
  }

  return {
    type: rawData?.type || 'hotel',
    details: {
      name:             rawData?.name || rawData?.hotelName || '',
      city:             rawData?.city || rawData?.location || '',
      address:          rawData?.address || '',
      checkInDate:      rawData?.checkInDate  || rawData?.checkIn  || null,
      checkOutDate:     rawData?.checkOutDate || rawData?.checkOut || null,
      phone:            rawData?.phone || '',
      url:              rawData?.url || '',
      roomType:         rawData?.roomType || '',
      bookingReference: rawData?.bookingReference || rawData?.confirmationNumber || '',
      bookingUrl:       rawData?.bookingUrl || '',
      bookingAgent:     rawData?.bookingAgent || '',
    },
    status:  rawData?.status || 'Planned',
    summary: rawData?.summary || rawData?.name || '',
    notes:   rawData?.notes || '',
    cost: rawData?.cost || { amount: 0, currency: 'USD', paid: false, dueDate: null }
  };
}

/**
 * Normalize AI-generated flat place data.
 * Places use a flat structure so this is mainly a pass-through, but we
 * ensure required fields are present.
 */
function normalizePlaceData(rawData) {
  return {
    name:     rawData?.name || '',
    address:  rawData?.address || '',
    notes:    rawData?.notes || '',
    category: rawData?.category || '',
    context:  rawData?.context || '',
    ...rawData
  };
}

/**
 * Combine a date string and an optional time string into a single ISO-like string.
 * If date already contains 'T' or ' ' it is returned as-is.
 */
function combineDateAndTime(date, time) {
  if (!date) return null;
  if (!time || date.includes('T') || date.includes(' ')) return date;
  return `${date}T${time}`;
}

/**
 * Build a type-specific `details` object from the AI's flat travel JSON.
 *
 * The AI generates flat fields (from, to, departDate, airline, etc.).
 * travelService and the Travel UI expect a nested `details` object whose
 * field names vary by transport type (departureAirport, departureStation,
 * departurePort, etc.).
 */
function buildTravelDetails(type, raw) {
  // departureTime: full datetime field, OR combine separate date+time components, OR null
  const departureTime = raw?.departureTime || combineDateAndTime(raw?.departDate, raw?.departTime) || null;
  // arrivalTime: full datetime field, OR just the arrival date if no time is known, OR null
  const arrivalTime   = raw?.arrivalTime   || raw?.arrivalDate || null;

  switch (type) {
    case 'flight':
      return {
        airline:          raw?.airline || raw?.carrier || '',
        flightNumber:     raw?.flightNumber || raw?.flight || '',
        departureAirport: raw?.departureAirport || raw?.from || '',
        arrivalAirport:   raw?.arrivalAirport   || raw?.to   || '',
        departureCity:    raw?.departureCity    || raw?.from || '',
        arrivalCity:      raw?.arrivalCity      || raw?.to   || '',
        departureTime,
        arrivalTime,
        bookingReference: raw?.bookingReference || '',
      };
    case 'train':
      return {
        operator:         raw?.operator || raw?.trainOperator || '',
        trainNumber:      raw?.trainNumber || raw?.serviceNumber || '',
        departureStation: raw?.departureStation || raw?.from || '',
        arrivalStation:   raw?.arrivalStation   || raw?.to   || '',
        departureTime,
        arrivalTime,
        bookingReference: raw?.bookingReference || '',
      };
    case 'bus':
      return {
        operator:      raw?.operator || '',
        serviceNumber: raw?.serviceNumber || raw?.busNumber || '',
        departureStop: raw?.departureStop || raw?.from || '',
        arrivalStop:   raw?.arrivalStop   || raw?.to   || '',
        departureTime,
        arrivalTime,
        bookingReference: raw?.bookingReference || '',
      };
    case 'ferry':
      return {
        operator:      raw?.operator || '',
        departurePort: raw?.departurePort || raw?.from || '',
        arrivalPort:   raw?.arrivalPort   || raw?.to   || '',
        departureTime,
        arrivalTime,
        bookingReference: raw?.bookingReference || '',
      };
    case 'car':
      return {
        rentalCompany:  raw?.rentalCompany || raw?.operator || '',
        pickupLocation: raw?.pickupLocation || raw?.from || '',
        returnLocation: raw?.returnLocation || raw?.to   || '',
        pickupTime:     raw?.pickupTime  || departureTime || null,
        returnTime:     raw?.returnTime  || arrivalTime   || null,
        vehicleClass:   raw?.vehicleClass || '',
        bookingReference: raw?.bookingReference || '',
      };
    case 'cruise':
      return {
        cruiseLine:    raw?.cruiseLine || raw?.operator || '',
        shipName:      raw?.shipName || '',
        departurePort: raw?.departurePort || raw?.from || '',
        arrivalPort:   raw?.arrivalPort   || raw?.to   || '',
        departureTime,
        arrivalTime,
        bookingReference: raw?.bookingReference || '',
      };
    case 'walk':
      return {
        name:              raw?.name || `Walk: ${raw?.from || ''} to ${raw?.to || ''}`.trim() || 'Walk',
        type:              'walk',
        departureLocation: raw?.departureLocation || raw?.from || '',
        arrivalLocation:   raw?.arrivalLocation   || raw?.to   || '',
        departureTime,
        arrivalTime,
      };
    default: // custom / unknown
      return {
        name:              raw?.name || `${raw?.from || ''} to ${raw?.to || ''}`.trim() || 'Transfer',
        type:              raw?.customType || type,
        departureLocation: raw?.departureLocation || raw?.from || '',
        arrivalLocation:   raw?.arrivalLocation   || raw?.to   || '',
        departureTime,
        arrivalTime,
      };
  }
}

/**
 * Generate a human-readable summary for a travel item when the AI hasn't provided one.
 * Mirrors the createSummary logic from TRAVEL_TYPES constants.
 */
function generateTravelSummary(type, raw, details) {
  const from = raw?.from || '';
  const to   = raw?.to   || '';
  switch (type) {
    case 'flight':
      return `${details.airline || 'Flight'} ${details.flightNumber || ''}: ${details.departureAirport || from} to ${details.arrivalAirport || to}`.replace(/\s+/g, ' ').trim();
    case 'train':
      return `${details.operator || 'Train'}: ${details.departureStation || from} to ${details.arrivalStation || to}`.trim();
    case 'bus':
      return `${details.operator || 'Bus'}: ${details.departureStop || from} to ${details.arrivalStop || to}`.trim();
    case 'ferry':
      return `${details.operator || 'Ferry'}: ${details.departurePort || from} to ${details.arrivalPort || to}`.trim();
    case 'car':
      return `${details.rentalCompany || 'Car'}: ${details.pickupLocation || from} to ${details.returnLocation || to}`.trim();
    case 'cruise':
      return `${details.cruiseLine || 'Cruise'}: ${details.departurePort || from} to ${details.arrivalPort || to}`.trim();
    case 'walk':
      return `Walk: ${details.departureLocation || from} to ${details.arrivalLocation || to}`.trim();
    default:
      return `${type}: ${from} to ${to}`.trim() || 'Transfer';
  }
}

/**
 * Normalize AI-generated flat travel data into the nested structure that
 * travelService and the Travel UI expect:
 *   { type, details: { ...type-specific fields }, status, summary, cost, notes }
 *
 * The AI produces flat JSON (from, to, departDate, airline, etc.).
 * travelService.fetchTravel sorts by details.departureTime and the Travel
 * detail components render details.airline, details.departureAirport, etc.
 * Without this normalization the Travel List crashes and all detail fields
 * appear blank.
 */
function normalizeTravelData(rawData) {
  // Already in the correct nested format — pass through unchanged
  if (rawData && rawData.details && typeof rawData.details === 'object') {
    return {
      type:    rawData.type    || 'custom',
      details: rawData.details,
      status:  rawData.status  || 'Planned',
      summary: rawData.summary || '',
      notes:   rawData.notes   || '',
      cost:    rawData.cost    || { amount: 0, currency: 'USD', paid: false, dueDate: null },
      createdAt: rawData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  const type    = rawData?.type || 'custom';
  const details = buildTravelDetails(type, rawData);
  const summary = rawData?.summary || generateTravelSummary(type, rawData, details);

  return {
    type,
    details,
    status:  rawData?.status || 'Planned',
    summary,
    notes:   rawData?.notes || '',
    cost:    rawData?.cost  || { amount: 0, currency: 'USD', paid: false, dueDate: null },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Normalize AI-generated day data.
 *
 * The Cloud Function prompt instructs the AI to use "description" for day
 * content, but DayDetailPage reads and writes "notes". Map description→notes
 * so approved Day proposals are not silently created with empty content.
 */
function normalizeDayData(rawData) {
  const { description, notes: rawNotes, name, ...rest } = rawData || {};
  return {
    ...rest,
    date:  rest.date  ?? null,
    title: rest.title || name || '',
    // AI writes "description"; UI reads "notes" — bridge the gap
    notes: rawNotes || description || '',
    order: rest.order ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Service Registry
// Maps action.service → action.method → async function(effectiveUserId, tripId, data)
// ---------------------------------------------------------------------------

const SERVICE_REGISTRY = {
  accommodation: {
    create: async (effectiveUserId, tripId, data) => {
      // Normalize flat AI data → nested structure the accommodation UI expects
      const normalized = normalizeAccommodationData(data);
      const newId = await addAccommodation(effectiveUserId, tripId, normalized);
      // Publish event so AccommodationList and suggestion services refresh
      eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_ADDED, {
        userId: effectiveUserId,
        tripId,
        id: newId,
        timestamp: new Date().toISOString(),
        ...normalized
      });
      return newId;
    },
    update: async (effectiveUserId, tripId, data, targetEntityId) => {
      if (!targetEntityId) throw new Error('accommodation:update requires targetEntityId');
      // Build a partial update — merge AI flat data into normalized structure
      // For updates we only send the fields actually present in data
      const { targetEntityId: _drop, ...updateFields } = data;
      // Normalize fields that map to nested paths (e.g. checkIn → details.checkInDate)
      const updatePayload = {};
      if (updateFields.city)          updatePayload['details.city']         = updateFields.city;
      if (updateFields.address)       updatePayload['details.address']      = updateFields.address;
      if (updateFields.checkIn)       updatePayload['details.checkInDate']  = updateFields.checkIn;
      if (updateFields.checkOut)      updatePayload['details.checkOutDate'] = updateFields.checkOut;
      if (updateFields.checkInDate)   updatePayload['details.checkInDate']  = updateFields.checkInDate;
      if (updateFields.checkOutDate)  updatePayload['details.checkOutDate'] = updateFields.checkOutDate;
      if (updateFields.name)          updatePayload['details.name']         = updateFields.name;
      if (updateFields.phone)         updatePayload['details.phone']        = updateFields.phone;
      if (updateFields.url)           updatePayload['details.url']          = updateFields.url;
      if (updateFields.roomType)      updatePayload['details.roomType']     = updateFields.roomType;
      if (updateFields.bookingReference) updatePayload['details.bookingReference'] = updateFields.bookingReference;
      if (updateFields.notes)         updatePayload['notes']                = updateFields.notes;
      if (updateFields.status)        updatePayload['status']               = updateFields.status;
      if (updateFields.type)          updatePayload['type']                 = updateFields.type;
      // Fall back to passing remaining unknown fields directly
      const knownFields = ['city','address','checkIn','checkOut','checkInDate','checkOutDate','name','phone','url','roomType','bookingReference','notes','status','type'];
      Object.keys(updateFields).forEach(k => {
        if (!knownFields.includes(k)) updatePayload[k] = updateFields[k];
      });
      await updateAccommodation(effectiveUserId, tripId, targetEntityId, updatePayload);
      eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_UPDATED || 'ACCOMMODATION_UPDATED', {
        userId: effectiveUserId, tripId, id: targetEntityId, timestamp: new Date().toISOString()
      });
      return targetEntityId;
    }
  },
  travel: {
    create: async (effectiveUserId, tripId, data) => {
      const normalized = normalizeTravelData(data);
      const newId = await addTravel(effectiveUserId, tripId, normalized);
      // Publish event so TravelList / suggestion services refresh
      if (EVENT_TYPES.TRAVEL_ADDED) {
        eventBridgeService.publish(EVENT_TYPES.TRAVEL_ADDED, {
          userId: effectiveUserId,
          tripId,
          id: newId,
          timestamp: new Date().toISOString(),
          ...normalized
        });
      }
      return newId;
    },
    update: async (effectiveUserId, tripId, data, targetEntityId) => {
      if (!targetEntityId) throw new Error('travel:update requires targetEntityId');
      const { targetEntityId: _drop, ...updateFields } = data;
      await updateTravel(effectiveUserId, tripId, targetEntityId, updateFields);
      return targetEntityId;
    }
  },
  place: {
    create: async (effectiveUserId, tripId, data) => {
      // Extract linkToAccommodationId before normalizing (don't write it to Firestore as a place field)
      const { linkToAccommodationId, ...placeData } = data || {};
      const normalized = normalizePlaceData(placeData);
      const newId = await addPlace(effectiveUserId, tripId, normalized);
      // Publish event so PlacesList / suggestion services refresh
      eventBridgeService.publish(EVENT_TYPES.PLACE_ADDED, {
        userId: effectiveUserId,
        tripId,
        id: newId,
        timestamp: new Date().toISOString(),
        ...normalized
      });

      // If a linkToAccommodationId was specified, link the new place to that accommodation
      if (linkToAccommodationId) {
        console.log(`[ProposalExecutionService] Linking new place ${newId} to accommodation ${linkToAccommodationId}`);
        const {
          connectionRegistryService,
          linkRegistryConfigService,
          LINK_TYPES,
          shouldRouteThroughRegistry
        } = await import('@trip-planner-monorepo/shared');

        const config = await linkRegistryConfigService.getLinkRegistryConfig(effectiveUserId, tripId);
        if (shouldRouteThroughRegistry(config)) {
          await connectionRegistryService.createLink(
            effectiveUserId,
            tripId,
            { type: 'accommodation', id: linkToAccommodationId },
            { type: 'place', id: newId },
            {
              linkType: LINK_TYPES.LOCATED_IN,
              metadata: { createdBy: 'travel_agent' },
              skipLegacyWrite: config.writeMode === 'registry_only'
            }
          );
        } else {
          await updateAccommodation(effectiveUserId, tripId, linkToAccommodationId, {
            placeReference: {
              placeId: newId,
              linkedAt: new Date().toISOString()
            },
            updatedAt: new Date().toISOString()
          });
          eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_PLACE_LINKED, {
            userId: effectiveUserId,
            tripId,
            accommodationId: linkToAccommodationId,
            placeId: newId,
            timestamp: new Date().toISOString()
          });
        }
        console.log(`[ProposalExecutionService] Linked accom ${linkToAccommodationId} → place ${newId}`);
      }

      return newId;
    },
    update: async (effectiveUserId, tripId, data, targetEntityId) => {
      if (!targetEntityId) throw new Error('place:update requires targetEntityId');
      const { targetEntityId: _drop, ...updateFields } = data;
      await updatePlace(effectiveUserId, tripId, targetEntityId, updateFields);
      return targetEntityId;
    }
  },
  day: {
    create: async (effectiveUserId, tripId, data) => {
      const normalized = normalizeDayData(data);
      const newId = await addDay(effectiveUserId, tripId, normalized);
      return newId;
    },
    update: async (effectiveUserId, tripId, data, targetEntityId) => {
      if (!targetEntityId) throw new Error('day:update requires targetEntityId');
      const { targetEntityId: _drop, ...updateFields } = data;
      
      const updatePayload = {};
      if (updateFields.date !== undefined) updatePayload.date = updateFields.date;
      if (updateFields.title !== undefined) updatePayload.title = updateFields.title;
      if (updateFields.name !== undefined && updateFields.title === undefined) updatePayload.title = updateFields.name;
      if (updateFields.notes !== undefined) updatePayload.notes = updateFields.notes;
      if (updateFields.description !== undefined && updateFields.notes === undefined) updatePayload.notes = updateFields.description;
      if (updateFields.order !== undefined) updatePayload.order = updateFields.order;

      const knownFields = ['date', 'title', 'name', 'notes', 'description', 'order'];
      Object.keys(updateFields).forEach(k => {
        if (!knownFields.includes(k)) updatePayload[k] = updateFields[k];
      });

      await updateDay(effectiveUserId, tripId, targetEntityId, updatePayload);
      
      if (EVENT_TYPES.DAY_UPDATED) {
        eventBridgeService.publish(EVENT_TYPES.DAY_UPDATED, {
          userId: effectiveUserId,
          tripId,
          id: targetEntityId,
          timestamp: new Date().toISOString()
        });
      }
      
      return targetEntityId;
    }
  }
};

// ---------------------------------------------------------------------------
// ProposalExecutionService class
// ---------------------------------------------------------------------------

class ProposalExecutionService {
  /**
   * Execute an approved proposal.
   *
   * Steps:
   * 1. Determine final data (modifiedData overrides suggestion.action.data)
   * 2. Look up the executor function in SERVICE_REGISTRY
   * 3. Call it with (effectiveUserId, tripId, finalData)
   * 4. Update suggestion status to 'approved' with the new entity ID
   *
   * @param {Object} params
   * @param {string} params.effectiveUserId - Trip owner's userId
   * @param {string} params.tripId - Trip identifier
   * @param {string} params.suggestionId - Firestore document ID of the suggestion
   * @param {Object} params.suggestion - Full suggestion data (from subscribeToSuggestions)
   * @param {string} params.currentUserId - UID of user performing the action
   * @param {Object|null} [params.modifiedData] - User modifications overriding action.data
   * @returns {Promise<string|null>} The ID of the created entity, or null on failure
   */
  async executeApproval({ effectiveUserId, tripId, suggestionId, suggestion, currentUserId, modifiedData = null }) {
    try {
      if (!effectiveUserId || !tripId || !suggestionId || !suggestion) {
        throw new Error('effectiveUserId, tripId, suggestionId, and suggestion are required');
      }

      const action = suggestion.action;

      // Cloud Function writes action.entityType + action.operation.
      // Earlier versions used action.service + action.method.
      // Support both field names for compatibility.
      const service = action?.service || action?.entityType;
      const method  = action?.method  || action?.operation;

      if (!service || !method) {
        throw new Error(`Suggestion ${suggestionId} has no valid action.service/method`);
      }

      // 1. Determine final data — modifiedData takes precedence
      const finalData = modifiedData || action?.data || {};

      // Extract targetEntityId — the AI embeds it in JSON_DATA for update/delete operations
      // Support both action.targetEntityId (top-level) and data.targetEntityId (embedded in JSON)
      const targetEntityId = action?.targetEntityId || finalData?.targetEntityId || null;

      // 2. Look up executor
      const serviceExecutors = SERVICE_REGISTRY[service];
      if (!serviceExecutors) {
        throw new Error(`Unknown service in proposal: "${service}"`);
      }
      const executor = serviceExecutors[method];
      if (typeof executor !== 'function') {
        throw new Error(`Unknown method "${method}" for service "${service}"`);
      }

      // 3. Execute — pass targetEntityId as 4th arg (used by update/delete handlers)
      console.log(`[ProposalExecutionService] Executing ${service}.${method} for suggestion ${suggestionId}${targetEntityId ? ` (target: ${targetEntityId})` : ''}`);
      const newEntityId = await executor(effectiveUserId, tripId, finalData, targetEntityId);

      // 4. Update status
      await agentSuggestionsService.updateSuggestionStatus(
        effectiveUserId, tripId, suggestionId,
        {
          status: 'approved',
          actionedByUserId: currentUserId,
          approvedEntityId: newEntityId || null,
          modifiedData: modifiedData || null
        }
      );

      console.log(`[ProposalExecutionService] Suggestion ${suggestionId} approved, entity: ${newEntityId}`);
      return newEntityId;
    } catch (error) {
      console.error('[ProposalExecutionService] executeApproval error:', error);
      throw error;
    }
  }

  /**
   * Dismiss a proposal without executing it.
   *
   * @param {Object} params
   * @param {string} params.effectiveUserId - Trip owner's userId
   * @param {string} params.tripId - Trip identifier
   * @param {string} params.suggestionId - Firestore document ID
   * @param {string} params.currentUserId - UID of user performing the action
   * @returns {Promise<void>}
   */
  async executeDismiss({ effectiveUserId, tripId, suggestionId, currentUserId }) {
    try {
      if (!effectiveUserId || !tripId || !suggestionId) {
        throw new Error('effectiveUserId, tripId, and suggestionId are required');
      }

      await agentSuggestionsService.updateSuggestionStatus(
        effectiveUserId, tripId, suggestionId,
        {
          status: 'dismissed',
          actionedByUserId: currentUserId
        }
      );

      console.log(`[ProposalExecutionService] Suggestion ${suggestionId} dismissed`);
    } catch (error) {
      console.error('[ProposalExecutionService] executeDismiss error:', error);
      throw error;
    }
  }
}

export const proposalExecutionService = new ProposalExecutionService();
export default proposalExecutionService;
